import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  ChevronRight, 
  Clock, 
  Calendar, 
  ChevronDown, 
  ChevronUp,
  CheckCircle2,
  Lightbulb,
  ArrowRight,
  FileText,
  Target,
  Layers,
  Construction,
  Wrench,
  Settings,
  Gauge,
  AlertTriangle,
  Cog,
  HardDrive,
  Activity,
  Ruler,
  ExternalLink,
  Monitor,
  Database,
  Shield,
  BarChart3,
  Wifi,
  Server,
  Clock3
} from 'lucide-react';

const DrillParameterRecorderGuide = () => {
  const [activeSection, setActiveSection] = useState('');
  const [openFaq, setOpenFaq] = useState(null);

  // Table of contents
  const sections = [
    { id: 'section-1', title: "Qu'est-ce qu'un enregistreur de paramètres ?" },
    { id: 'section-2', title: "Pourquoi est-ce indispensable ?" },
    { id: 'section-3', title: "Quels paramètres sont enregistrés ?" },
    { id: 'section-4', title: "Comment ça marche ?" },
    { id: 'section-5', title: "Cas d'usage par procédé" },
    { id: 'section-6', title: "Questions fréquentes" },
    { id: 'section-7', title: "Bien s'équiper" },
  ];

  const faqs = [
    {
      question: "L'enregistrement de paramètres est-il obligatoire ?",
      answer: "Il fait partie du contrôle interne d'exécution attendu dans le cadre de la mission G3 (norme NF P 94-500) et des contrôles prévus par la norme d'exécution NF EN 1536 pour les pieux forés. Au-delà, le cahier des charges du marché impose fréquemment son usage. En pratique, il est devenu un standard incontournable sur les chantiers de fondations spéciales."
    },
    {
      question: "Quelle différence avec une fiche de forage manuelle ?",
      answer: "La fiche manuelle est déclarative et sujette à erreur ou approximation. L'enregistrement de paramètres est automatique, horodaté et continu : il capte la réalité de l'exécution en temps réel, sans intervention humaine, ce qui le rend beaucoup plus fiable comme preuve."
    },
    {
      question: "Peut-on consulter les données à distance ?",
      answer: "Oui. Les systèmes modernes permettent la transmission des enregistrements vers une plateforme accessible en ligne, pour un suivi du chantier et un contrôle qualité à distance, en temps réel ou en différé."
    },
    {
      question: "Qui exploite ces données ?",
      answer: "L'opérateur en cabine (contrôle immédiat), le conducteur de travaux et le bureau de supervision (contrôle qualité), et in fine le maître d'œuvre ou le contrôleur technique dans le cadre de la validation de l'ouvrage."
    },
    {
      question: "Faut-il faire étalonner son matériel de mesure ?",
      answer: "Oui, impérativement et régulièrement. Un capteur mal étalonné fausse toute l'interprétation des données et peut invalider le contrôle qualité, voire poser problème en cas de litige."
    }
  ];

  // Track scroll for active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
  };

  // Schema.org JSON-LD
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Enregistreur de paramètres de forage : DAC, contrôle et traçabilité | Guide",
    "description": "Tout sur l'enregistreur de paramètres de forage (EPF) : rôle, paramètres mesurés, contrôle qualité des pieux, conformité NF EN 1536 et traçabilité des fondations.",
    "image": "https://geots.consulting/og-images/guide-enregistreur-parametres.jpg",
    "author": {
      "@type": "Organization",
      "name": "GEOTS Consulting"
    },
    "publisher": {
      "@type": "Organization",
      "name": "GEOTS Consulting",
      "logo": {
        "@type": "ImageObject",
        "url": "https://geots.consulting/logo.png"
      }
    },
    "datePublished": "2026-07-18",
    "dateModified": "2026-07-18"
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  // Parameters table data
  const parameters = [
    { param: "Profondeur", meaning: "Position de l'outil dans le sol", icon: Ruler },
    { param: "Vitesse d'avancement / de rotation", meaning: "Progression du forage", icon: Gauge },
    { param: "Couple de rotation", meaning: "Résistance rencontrée, dureté du terrain", icon: Cog },
    { param: "Pression (poussée, injection, retenue)", meaning: "Effort appliqué, qualité de l'injection", icon: Activity },
    { param: "Vitesse d'extraction de la tarière", meaning: "Cruciale en tarière creuse (CFA)", icon: Clock3 },
    { param: "Volume et débit de béton / coulis", meaning: "Contrôle du remplissage, détection de sur- ou sous-consommation", icon: BarChart3 },
    { param: "Inclinaison / déviation", meaning: "Verticalité de l'ouvrage", icon: Target },
  ];

  // System components
  const systemComponents = [
    { 
      number: 1, 
      title: "Les capteurs", 
      desc: "Installés sur la machine et l'outil : capteurs de pression, de couple, de profondeur, d'inclinaison. Certains se placent au plus près de l'action — en bas de l'outil, sur la tarière ou le Kelly.", 
      icon: Settings 
    },
    { 
      number: 2, 
      title: "L'interface en cabine", 
      desc: "Un boîtier d'affichage et de calcul qui présente les paramètres à l'opérateur en temps réel et pilote l'enregistrement. C'est le tableau de bord du foreur.", 
      icon: Monitor 
    },
    { 
      number: 3, 
      title: "La supervision et transfert", 
      desc: "Les mesures peuvent être transmises vers les bureaux de supervision, consultées à distance via une plateforme en ligne, et éditées sous forme de rapports.", 
      icon: Wifi 
    },
  ];

  // Use cases by process
  const useCases = [
    { process: "Pieux forés (Kelly, tarière)", params: "Profondeur, couple, vitesse, volume de béton", icon: Construction },
    { process: "Tarière creuse / CFA", params: "Contrôle de la vitesse d'extraction et de la pression de béton lors du retrait — paramètre critique pour éviter les défauts de continuité", icon: Cog },
    { process: "Micropieux et injection", params: "Pression et volume de coulis injecté, essentiels pour valider le scellement", icon: Target },
    { process: "Colonnes de jet-grouting, soil mixing", params: "Suivi de l'énergie et des débits", icon: Activity },
    { process: "Parois moulées", params: "Mesure de la déviation de l'outil (benne, hydrofraise) pour garantir la verticalité", icon: Layers },
  ];

  // Benefits
  const benefits = [
    { title: "Traçabilité de l'ouvrage caché", desc: "Un pieu foré ne se revisite pas une fois coulé. L'enregistrement fournit un relevé horodaté et infalsifiable de son exécution.", icon: Database },
    { title: "Contrôle qualité en temps réel", desc: "L'opérateur voit en cabine, pendant le forage, si les paramètres restent dans les seuils prévus. Détection immédiate des anomalies.", icon: Monitor },
    { title: "Conformité normative", desc: "Contrôle interne d'exécution dans le cadre de la mission G3 (NF P 94-500) et de la norme NF EN 1536 pour les pieux forés.", icon: Shield },
    { title: "Preuve en cas de litige", desc: "En cas de désordre ou de contestation, l'enregistrement constitue un élément de preuve technique déterminant.", icon: FileText },
  ];

  return (
    <>
      <Helmet>
        <title>Enregistreur de paramètres de forage : DAC, contrôle et traçabilité | Guide</title>
        <meta name="description" content="Tout sur l'enregistreur de paramètres de forage (EPF) : rôle, paramètres mesurés, contrôle qualité des pieux, conformité NF EN 1536 et traçabilité des fondations." />
        <link rel="canonical" href="https://geots.consulting/guides/enregistreur-parametres-forage" />
        <meta property="og:title" content="Enregistreur de paramètres de forage : DAC, contrôle et traçabilité | Guide" />
        <meta property="og:description" content="Tout sur l'enregistreur de paramètres de forage (EPF) : rôle, paramètres mesurés, contrôle qualité des pieux, conformité NF EN 1536 et traçabilité des fondations." />
        <meta property="og:url" content="https://geots.consulting/guides/enregistreur-parametres-forage" />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-[#0A2540] via-[#0A2540] to-[#1a3a5c] text-white">
          <div className="container mx-auto px-4 py-12 md:py-20">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm mb-8 text-gray-300">
              <Link to="/" className="hover:text-white">Accueil</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/guides" className="hover:text-white">Guides</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-white">Enregistreur de paramètres</span>
            </nav>

            <div className="max-w-4xl">
              <div className="inline-flex items-center gap-2 bg-[#635BFF]/20 text-[#00D4FF] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Gauge className="w-4 h-4" />
                Guide technique
              </div>
              
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
                L'enregistreur de paramètres de forage : guide complet
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#00D4FF] to-[#635BFF] mt-2">
                  (EPF, contrôle et traçabilité)
                </span>
              </h1>

              <p className="text-xl text-gray-300 mb-8 leading-relaxed max-w-3xl">
                Sur un chantier de fondations spéciales, comment prouver qu'un pieu a été correctement exécuté, 
                alors que l'ouvrage est enterré et invisible ? La réponse tient dans un boîtier : l'enregistreur de paramètres de forage. 
                Traçabilité, contrôle qualité, conformité normative — ce guide fait le tour de cet outil devenu incontournable.
              </p>

              <div className="flex flex-wrap items-center gap-6 text-sm text-gray-400">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>Mis à jour : Juillet 2026</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>Temps de lecture : 8 min</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-12">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Sidebar - Table of Contents */}
            <aside className="lg:w-72 flex-shrink-0">
              <div className="lg:sticky lg:top-24">
                <div className="bg-white rounded-xl shadow-lg border p-6">
                  <h3 className="font-bold text-[#0A2540] mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#635BFF]" />
                    Sommaire
                  </h3>
                  <nav className="space-y-1">
                    {sections.map((section, index) => (
                      <button
                        key={section.id}
                        onClick={() => scrollToSection(section.id)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all flex items-center gap-2 ${
                          activeSection === section.id
                            ? 'bg-[#635BFF]/10 text-[#635BFF] font-medium'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <span className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium">
                          {index + 1}
                        </span>
                        <span className="line-clamp-2">{section.title}</span>
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Related Guides */}
                <div className="bg-gradient-to-br from-[#635BFF]/5 to-[#00D4FF]/5 rounded-xl p-6 mt-6 border border-[#635BFF]/10">
                  <h4 className="font-bold text-[#0A2540] mb-4">Guides connexes</h4>
                  <div className="space-y-3">
                    <Link to="/guides/comment-choisir-fondations-speciales" className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#635BFF] transition-colors">
                      <ArrowRight className="w-4 h-4" />
                      Choisir ses fondations spéciales
                    </Link>
                    <Link to="/guides/paroi-moulee-technique-realisation" className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#635BFF] transition-colors">
                      <ArrowRight className="w-4 h-4" />
                      Paroi moulée : technique et réalisation
                    </Link>
                    <Link to="/guides/materiel-de-forage" className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#635BFF] transition-colors">
                      <ArrowRight className="w-4 h-4" />
                      Matériel de forage complet
                    </Link>
                    <Link to="/guides/comment-choisir-foreuse-geotechnique" className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#635BFF] transition-colors">
                      <ArrowRight className="w-4 h-4" />
                      Choisir sa foreuse géotechnique
                    </Link>
                  </div>
                </div>
              </div>
            </aside>

            {/* Main Article */}
            <article className="flex-1 max-w-3xl">
              {/* Section 1: What is it? */}
              <section id="section-1" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-[#635BFF]/10 flex items-center justify-center">
                    <HardDrive className="w-5 h-5 text-[#635BFF]" />
                  </span>
                  Qu'est-ce qu'un enregistreur de paramètres de forage ?
                </h2>

                <div className="prose prose-lg max-w-none text-gray-700">
                  <p>
                    Un enregistreur de paramètres de forage (souvent appelé <strong>DAC</strong>, pour Dispositif d'Acquisition 
                    et de Contrôle, ou « enregistreur de paramètres ») est un système embarqué sur la machine de forage qui 
                    <strong> mesure, affiche et enregistre en temps réel</strong> les grandeurs caractéristiques de l'exécution 
                    d'un pieu, d'un micropieu, d'une paroi ou d'une injection.
                  </p>

                  <div className="bg-gradient-to-r from-[#635BFF]/5 to-[#00D4FF]/5 rounded-xl p-6 my-6 border-l-4 border-[#635BFF]">
                    <p className="font-medium text-[#0A2540] mb-2 flex items-center gap-2">
                      <Lightbulb className="w-5 h-5 text-[#635BFF]" />
                      Le principe
                    </p>
                    <p className="text-gray-700 mb-0">
                      Capter, à la source, ce que fait réellement l'outil dans le sol. Vitesse d'avancement, profondeur, 
                      couple de rotation, pression, débit de béton ou de coulis… Autant de paramètres qui, mis bout à bout, 
                      constituent la <strong>carte d'identité de l'ouvrage</strong> — une preuve objective de sa bonne exécution.
                    </p>
                  </div>

                  <p>
                    Comme l'ouvrage fini est enterré et invisible, l'enregistrement de paramètres est souvent le 
                    <strong> seul témoin fiable</strong> de ce qui s'est passé pendant le forage et le bétonnage. 
                    D'où son rôle central dans le contrôle qualité.
                  </p>
                </div>
              </section>

              {/* Section 2: Why is it essential? */}
              <section id="section-2" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  </span>
                  Pourquoi est-ce indispensable ?
                </h2>

                <div className="grid gap-6">
                  {benefits.map((benefit, index) => {
                    const IconComponent = benefit.icon;
                    return (
                      <div 
                        key={index}
                        className="bg-white rounded-xl p-6 border shadow-sm hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-xl bg-[#635BFF]/10 flex items-center justify-center flex-shrink-0">
                            <IconComponent className="w-6 h-6 text-[#635BFF]" />
                          </div>
                          <div>
                            <h3 className="font-bold text-[#0A2540] mb-2">{benefit.title}</h3>
                            <p className="text-gray-600">{benefit.desc}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-8 bg-amber-50 border border-amber-200 rounded-xl p-6">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-amber-800 mb-2">Conformité normative</h4>
                      <p className="text-amber-700">
                        Le contrôle interne d'exécution des pieux forés, dans le cadre de la mission géotechnique <strong>G3</strong> 
                        (confiée à l'entreprise selon la norme <strong>NF P 94-500</strong>), comprend explicitement les 
                        enregistrements de paramètres, au même titre que les fiches de bétonnage. La norme d'exécution 
                        européenne <strong>NF EN 1536</strong> (pieux forés) encadre ces contrôles d'exécution.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Section 3: Parameters recorded */}
              <section id="section-3" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-blue-600" />
                  </span>
                  Quels paramètres sont enregistrés ?
                </h2>

                <p className="text-gray-700 mb-6">
                  Les grandeurs varient selon le procédé, mais on retrouve typiquement :
                </p>

                <div className="bg-white rounded-xl border overflow-hidden shadow-sm">
                  <table className="w-full">
                    <thead className="bg-[#0A2540] text-white">
                      <tr>
                        <th className="px-6 py-4 text-left font-semibold">Paramètre</th>
                        <th className="px-6 py-4 text-left font-semibold">Ce qu'il traduit</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {parameters.map((param, index) => {
                        const IconComponent = param.icon;
                        return (
                          <tr key={index} className="hover:bg-gray-50 transition-colors">
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <IconComponent className="w-5 h-5 text-[#635BFF]" />
                                <span className="font-medium text-[#0A2540]">{param.param}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-gray-600">{param.meaning}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="mt-6 bg-gradient-to-r from-[#635BFF]/5 to-[#00D4FF]/5 rounded-xl p-6 border-l-4 border-[#00D4FF]">
                  <p className="text-gray-700 mb-0">
                    <strong>Le croisement de ces données</strong> permet, par exemple, de vérifier le profil de bétonnage 
                    d'un pieu et de détecter d'éventuels défauts (striction, sur-consommation localisée).
                  </p>
                </div>
              </section>

              {/* Section 4: How it works */}
              <section id="section-4" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                    <Settings className="w-5 h-5 text-purple-600" />
                  </span>
                  Comment ça marche : capteurs, interface, supervision
                </h2>

                <p className="text-gray-700 mb-8">
                  Un système d'enregistrement se compose de trois maillons :
                </p>

                <div className="space-y-6">
                  {systemComponents.map((comp, index) => {
                    const IconComponent = comp.icon;
                    return (
                      <div 
                        key={index}
                        className="flex items-start gap-4 bg-white rounded-xl p-6 border shadow-sm"
                      >
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#635BFF] to-[#00D4FF] flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                          {comp.number}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <IconComponent className="w-5 h-5 text-[#635BFF]" />
                            <h3 className="font-bold text-[#0A2540]">{comp.title}</h3>
                          </div>
                          <p className="text-gray-600">{comp.desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-8 bg-gray-50 rounded-xl p-6 border">
                  <h4 className="font-bold text-[#0A2540] mb-3 flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-[#635BFF]" />
                    Bon à savoir
                  </h4>
                  <p className="text-gray-700 mb-3">
                    Sur les pieux réalisés au Kelly, les données peuvent être transmises en quasi temps réel à chaque remontée d'outil.
                  </p>
                  <p className="text-gray-700 mb-0">
                    Sur les pieux à la tarière continue (CFA), l'enregistrement complet est généralement disponible une fois 
                    le pieu terminé (forage + bétonnage).
                  </p>
                </div>
              </section>

              {/* Section 5: Use cases */}
              <section id="section-5" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                    <Construction className="w-5 h-5 text-orange-600" />
                  </span>
                  Les cas d'usage par procédé
                </h2>

                <div className="space-y-4">
                  {useCases.map((useCase, index) => {
                    const IconComponent = useCase.icon;
                    return (
                      <div 
                        key={index}
                        className="bg-white rounded-xl p-5 border hover:border-[#635BFF]/50 hover:shadow-md transition-all"
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 rounded-lg bg-[#635BFF]/10 flex items-center justify-center flex-shrink-0">
                            <IconComponent className="w-5 h-5 text-[#635BFF]" />
                          </div>
                          <div>
                            <h3 className="font-bold text-[#0A2540] mb-1">{useCase.process}</h3>
                            <p className="text-gray-600 text-sm">{useCase.params}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Cross-links to related guides */}
                <div className="mt-8 grid md:grid-cols-2 gap-4">
                  <Link 
                    to="/guides/comment-choisir-fondations-speciales"
                    className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl hover:bg-blue-100 transition-colors"
                  >
                    <Construction className="w-6 h-6 text-blue-600" />
                    <div>
                      <p className="font-semibold text-blue-800">Fondations spéciales</p>
                      <p className="text-sm text-blue-600">Guide complet du choix</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-blue-600 ml-auto" />
                  </Link>
                  <Link 
                    to="/guides/paroi-moulee-technique-realisation"
                    className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl hover:bg-green-100 transition-colors"
                  >
                    <Layers className="w-6 h-6 text-green-600" />
                    <div>
                      <p className="font-semibold text-green-800">Paroi moulée</p>
                      <p className="text-sm text-green-600">Technique et réalisation</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-green-600 ml-auto" />
                  </Link>
                </div>
              </section>

              {/* Section 6: FAQ */}
              <section id="section-6" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center">
                    <Lightbulb className="w-5 h-5 text-teal-600" />
                  </span>
                  Questions fréquentes
                </h2>

                <div className="space-y-4">
                  {faqs.map((faq, index) => (
                    <div 
                      key={index}
                      className="bg-white rounded-xl border overflow-hidden"
                    >
                      <button
                        onClick={() => setOpenFaq(openFaq === index ? null : index)}
                        className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
                      >
                        <span className="font-semibold text-[#0A2540] pr-4">{faq.question}</span>
                        {openFaq === index ? (
                          <ChevronUp className="w-5 h-5 text-[#635BFF] flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        )}
                      </button>
                      {openFaq === index && (
                        <div className="px-5 pb-5 text-gray-600 border-t">
                          <p className="pt-4">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>

              {/* Section 7: Get equipped */}
              <section id="section-7" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-[#635BFF]/10 flex items-center justify-center">
                    <Wrench className="w-5 h-5 text-[#635BFF]" />
                  </span>
                  Bien s'équiper en enregistreurs de paramètres
                </h2>

                <div className="prose prose-lg max-w-none text-gray-700">
                  <p>
                    Le choix et le bon étalonnage d'un enregistreur de paramètres, ainsi que de ses capteurs, conditionnent 
                    directement la fiabilité du contrôle qualité. Un capteur mal calibré fausse toute l'interprétation.
                  </p>
                  <p>
                    <strong>Location, calibration, maintenance et formation</strong> à l'exploitation des mesures sont des 
                    maillons essentiels de la chaîne.
                  </p>
                </div>

                {/* CTA Box */}
                <div className="mt-8 bg-gradient-to-br from-[#0A2540] to-[#1a3a5c] rounded-2xl p-8 text-white">
                  <h3 className="text-xl font-bold mb-4">
                    Besoin d'un enregistreur de paramètres ?
                  </h3>
                  <p className="text-gray-300 mb-6">
                    GEOTS met en relation les acteurs de la géotechnique et des fondations spéciales : entreprises de forage, 
                    bureaux d'études, loueurs et spécialistes de l'instrumentation. Besoin d'un enregistreur de paramètres, 
                    de capteurs, d'un étalonnage ou d'un accompagnement sur la traçabilité de vos forages ?
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <Link 
                      to="/contact"
                      className="inline-flex items-center gap-2 bg-[#635BFF] hover:bg-[#5248d9] text-white px-6 py-3 rounded-lg font-medium transition-colors"
                    >
                      Contactez-nous
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                    <Link 
                      to="/annuaire/ingenierie-etudes-geotechniques/instrumentation-monitoring"
                      className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                    >
                      Voir l'annuaire instrumentation
                      <ExternalLink className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </section>

              {/* Disclaimer */}
              <div className="text-sm text-gray-500 italic border-t pt-8">
                Ce guide est fourni à titre informatif. La mise en œuvre du contrôle d'exécution des fondations profondes 
                doit s'appuyer sur les normes en vigueur (NF EN 1536, NF P 94-500) et l'avis de professionnels qualifiés.
              </div>
            </article>
          </div>
        </div>

        {/* Related Guides Section */}
        <div className="bg-gray-50 border-t py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-8 text-center">
              Continuez votre lecture
            </h2>
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              <Link 
                to="/guides/comment-choisir-fondations-speciales"
                className="bg-white rounded-xl p-6 border hover:shadow-lg transition-shadow group"
              >
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mb-4">
                  <Construction className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-bold text-[#0A2540] mb-2 group-hover:text-[#635BFF] transition-colors">
                  Comment choisir ses fondations spéciales ?
                </h3>
                <p className="text-gray-600 text-sm">
                  Micropieux, pieux forés, injections... Quel procédé pour quel projet ?
                </p>
              </Link>

              <Link 
                to="/guides/paroi-moulee-technique-realisation"
                className="bg-white rounded-xl p-6 border hover:shadow-lg transition-shadow group"
              >
                <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center mb-4">
                  <Layers className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="font-bold text-[#0A2540] mb-2 group-hover:text-[#635BFF] transition-colors">
                  Paroi moulée : technique et réalisation
                </h3>
                <p className="text-gray-600 text-sm">
                  Guide complet sur les parois moulées et barrettes.
                </p>
              </Link>

              <Link 
                to="/guides/materiel-de-forage"
                className="bg-white rounded-xl p-6 border hover:shadow-lg transition-shadow group"
              >
                <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center mb-4">
                  <Wrench className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="font-bold text-[#0A2540] mb-2 group-hover:text-[#635BFF] transition-colors">
                  Matériel de forage : guide complet
                </h3>
                <p className="text-gray-600 text-sm">
                  Machines, outils et instrumentation de forage.
                </p>
              </Link>

              <Link 
                to="/guides/etalonnage-capteurs-forage"
                className="bg-white rounded-xl p-6 border hover:shadow-lg transition-shadow group"
              >
                <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mb-4">
                  <Settings className="w-6 h-6 text-slate-600" />
                </div>
                <h3 className="font-bold text-[#0A2540] mb-2 group-hover:text-[#635BFF] transition-colors">
                  Étalonnage des capteurs de forage
                </h3>
                <p className="text-gray-600 text-sm">
                  Obligations, norme ISO 17025 et bonnes pratiques.
                </p>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DrillParameterRecorderGuide;
